import { storageService } from '../firebaseInstance';
import axios from 'axios';

/**
 * Firebase Storage에 미디어 파일 업로드
 * @param {File} file - 업로드할 파일
 * @param {string} path - 저장 경로 (예: 'threads/userId/temp')
 * @param {Function} onProgress - 진행률 콜백 (0-100)
 * @returns {Promise<Object>} 업로드된 파일 정보
 */
export const uploadMediaToFirebase = async (file, path, onProgress) => {
  try {
    // 파일 유효성 검사
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid file object provided');
    }
    
    if (file.size === 0) {
      throw new Error('File is empty (0 bytes)');
    }
    
    console.log('📁 Uploading file:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
    
    // 파일을 ArrayBuffer로 읽어서 안전하게 보관
    const fileBuffer = await file.arrayBuffer();
    const blob = new Blob([fileBuffer], { type: file.type });
    
    console.log('📦 File converted to blob:', {
      originalSize: file.size,
      blobSize: blob.size,
      type: blob.type
    });
    
    if (blob.size !== file.size) {
      throw new Error(`File corruption detected: Original ${file.size}B, Blob ${blob.size}B`);
    }
    
    // 파일명 중복 방지를 위한 타임스탬프 추가
    const timestamp = Date.now();
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}_${sanitizedName}`;
    
    // Storage 참조 생성
    const storageRef = storageService.ref(`${path}/${fileName}`);
    
    // Blob을 사용하여 업로드 (File 객체 대신)
    const uploadTask = storageRef.put(blob, {
      contentType: file.type,
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString()
      }
    });
    
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        // 진행률 업데이트
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(progress);
          }
        },
        // 에러 처리
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        // 완료 처리
        async () => {
          try {
            const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
            const uploadedSize = uploadTask.snapshot.totalBytes;
            
            console.log('✅ File uploaded successfully:', {
              fileName,
              originalSize: file.size,
              uploadedSize: uploadedSize,
              url: downloadURL
            });
            
            // 업로드된 크기와 원본 크기 비교
            if (uploadedSize !== file.size) {
              console.warn(`⚠️ Size mismatch: Original ${file.size} bytes, Uploaded ${uploadedSize} bytes`);
            }
            
            if (uploadedSize < 100) {
              console.error(`❌ Upload failed: File too small (${uploadedSize} bytes)`);
              reject(new Error(`Upload failed: File corrupted or too small (${uploadedSize} bytes)`));
              return;
            }
            
            resolve({
              url: downloadURL,
              path: uploadTask.snapshot.ref.fullPath,
              fileName,
              type: file.type,
              size: uploadedSize, // 실제 업로드된 크기 사용
              originalSize: file.size,
              originalName: file.name
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Failed to start upload:', error);
    throw error;
  }
};

/**
 * Firebase Storage에서 미디어 파일 삭제
 * @param {string} path - 삭제할 파일 경로
 */
export const deleteMediaFromFirebase = async (path) => {
  try {
    const storageRef = storageService.ref(path);
    await storageRef.delete();
  } catch (error) {
    console.error('Delete error:', error);
    // 파일이 존재하지 않는 경우 에러를 무시
    if (error.code !== 'storage/object-not-found') {
      throw error;
    }
  }
};

/**
 * 여러 미디어 파일 일괄 업로드
 * @param {Array<File>} files - 업로드할 파일 배열
 * @param {string} path - 저장 경로
 * @param {Function} onProgressUpdate - 각 파일 진행률 업데이트 콜백
 * @returns {Promise<Array>} 업로드된 파일 정보 배열
 */
export const uploadMultipleMedia = async (files, path, onProgressUpdate) => {
  const uploadPromises = files.map((file, index) => {
    return uploadMediaToFirebase(file, path, (progress) => {
      if (onProgressUpdate) {
        onProgressUpdate(index, progress);
      }
    });
  });
  
  return Promise.all(uploadPromises);
};

/**
 * 임시 미디어 파일 정리
 * @param {Array<Object>} uploadedMedia - 업로드된 미디어 정보 배열
 */
export const cleanupTempMedia = async (uploadedMedia) => {
  const deletePromises = uploadedMedia.map(media => 
    deleteMediaFromFirebase(media.path)
  );
  
  await Promise.allSettled(deletePromises);
};

/**
 * 파일 검증
 * @param {File} file - 검증할 파일
 * @param {Function} t - 번역 함수
 * @returns {Object} 검증 결과
 */
export const validateMediaFile = (file, t) => {
  // 🔥 파일 정보 로깅 (디버깅용)
  console.log('🔍 File validation:', {
    name: file.name,
    type: file.type,
    size: file.size,
    lastModified: file.lastModified
  });

  // 🔥 Threads API 공식 스펙에 맞춘 파일 타입 (정확한 MIME 타입만)
  const allowedImageTypes = ['image/jpeg', 'image/png']; // JPEG, PNG만 허용
  const allowedVideoTypes = [
    'video/mp4', 
    'video/quicktime',
    'video/mov', // MOV 파일의 다른 MIME 타입
    'video/x-msvideo', // AVI (일부 브라우저에서 MP4로 잘못 인식하는 경우)
    'video/webm' // WebM (일부 시스템에서 지원)
  ]; // 더 다양한 비디오 형식 허용
  
  // 🔥 Threads API 공식 스펙에 맞춘 파일 크기
  const maxImageSize = 8 * 1024 * 1024; // 8MB
  const maxVideoSize = 300 * 1024 * 1024; // 300MB (제한 변경)
  
  // 파일 타입과 확장자 확인 (이중 검증)
  const isImage = allowedImageTypes.includes(file.type);
  const isVideo = allowedVideoTypes.includes(file.type);
  
  // 파일 확장자 검증 (추가 보안)
  const fileName = file.name.toLowerCase();
  const allowedImageExtensions = ['.jpg', '.jpeg', '.png'];
  const allowedVideoExtensions = ['.mp4', '.mov', '.avi', '.webm'];
  
  const hasValidImageExtension = allowedImageExtensions.some(ext => fileName.endsWith(ext));
  const hasValidVideoExtension = allowedVideoExtensions.some(ext => fileName.endsWith(ext));
  
  // MIME 타입과 확장자 검증 결과 로깅
  console.log('🔍 Validation results:', {
    isImage,
    isVideo,
    hasValidImageExtension,
    hasValidVideoExtension,
    fileName
  });
  
  // MIME 타입과 확장자가 모두 유효해야 함
  const isValidImage = isImage && hasValidImageExtension;
  const isValidVideo = isVideo && hasValidVideoExtension;
  
  if (!isValidImage && !isValidVideo) {
    return {
      valid: false,
      error: t ? t('postThreads.media.validation.unsupportedFormatDetails') : 'JPEG, PNG, MP4, MOV 파일만 업로드 가능합니다. (GIF, SVG, WebP 등은 지원되지 않습니다)'
    };
  }
  
  // 파일 크기 확인
  const maxSize = isValidVideo ? maxVideoSize : maxImageSize;
  if (file.size > maxSize) {
    const currentSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    if (t) {
      return {
        valid: false,
        error: isValidVideo 
          ? t('postThreads.media.validation.videoTooLarge', { currentSize: currentSizeMB })
          : t('postThreads.media.validation.imageTooLarge', { currentSize: currentSizeMB })
      };
    } else {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
      return {
        valid: false,
        error: `파일 크기가 너무 큽니다. 최대 ${maxSizeMB}MB까지 업로드 가능합니다. (현재: ${currentSizeMB}MB)`
      };
    }
  }
  
  return { valid: true };
};

/**
 * URL 접근성 확인 (HTTP 상태 기반)
 * @param {string} url - 확인할 URL
 * @returns {Promise<boolean>} URL 접근 가능 여부
 */
export const isUrlAccessible = async (url) => {
  try {
    const response = await axios.head(url, {
      timeout: 10000,
      headers: {
        'User-Agent': 'Mozilla/5.0 (compatible; ThreadsBot/1.0)'
      }
    });
    console.log(`✅ URL accessible: ${url} - Status: ${response.status}`);
    return response.status === 200;
  } catch (error) {
    // 정확한 실패 원인 로깅
    console.error(`❌ URL not accessible: ${url}`);
    console.error(`Error details:`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      code: error.code,
      message: error.message,
      headers: error.response?.headers
    });
    return false;
  }
};

/**
 * 조건이 충족될 때까지 대기하는 표준 템플릿
 * @param {Function} checkFunction - 확인 함수
 * @param {Object} options - 옵션
 * @returns {Promise<any>} 조건 충족 시 결과
 */
export const waitForCondition = async (checkFunction, options = {}) => {
  const { maxAttempts = 30, checkInterval = 1000, name = 'condition' } = options;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    console.log(`🔍 Checking ${name} (attempt ${attempt}/${maxAttempts})`);
    
    try {
      const result = await checkFunction();
      
      if (result) {
        console.log(`✅ ${name} satisfied after ${attempt} attempts`);
        return result;
      }
      
      // 조건 불충족 시에만 계속
      console.log(`⏳ ${name} not ready, checking again...`);
      
    } catch (error) {
      console.log(`❌ ${name} check failed:`, error.message);
      
      // 특정 오류는 즉시 포기 (예: 401, 403, 500)
      if (error.response?.status && ![404, 429].includes(error.response.status)) {
        throw new Error(`${name} check failed permanently: ${error.message}`);
      }
    }
    
    // 마지막 시도에서도 실패하면 에러
    if (attempt === maxAttempts) {
      throw new Error(`${name} not satisfied after ${maxAttempts} attempts`);
    }
    
    // 상태 확인 간격 (유일하게 허용되는 setTimeout)
    await new Promise(resolve => setTimeout(resolve, checkInterval));
  }
};

/**
 * Firebase Storage URL 준비 상태 확인 (조건부 로직)
 * @param {string} url - Firebase Storage URL
 * @returns {Promise<boolean>} URL 준비 완료 여부
 */
export const waitForFirebaseUrlReady = async (url) => {
  console.log(`🔄 Starting Firebase Storage URL accessibility check: ${url}`);
  
  // URL 구조 분석
  const urlAnalysis = {
    isFirebaseStorage: url.includes('firebasestorage.googleapis.com'),
    hasToken: url.includes('token='),
    hasAltMedia: url.includes('alt=media'),
    domain: new URL(url).hostname
  };
  console.log(`📊 URL Analysis:`, urlAnalysis);
  
  try {
    // 즉시 한 번 시도해보기
    console.log(`🔍 Immediate accessibility check...`);
    const immediateResult = await isUrlAccessible(url);
    if (immediateResult) {
      console.log(`✅ URL immediately accessible!`);
      return true;
    }
    
    console.log(`⏳ URL not immediately accessible, starting condition check...`);
    await waitForCondition(() => isUrlAccessible(url), {
      name: 'Firebase Storage URL accessibility',
      maxAttempts: 30, // 30초로 증가
      checkInterval: 1000
    });
    return true;
  } catch (error) {
    console.error(`❌ Firebase Storage URL accessibility failed after all attempts`);
    console.error(`Failed URL: ${url}`);
    console.error(`Error: ${error.message}`);
    throw new Error(`Firebase Storage URL이 준비되지 않았습니다: ${url}`);
  }
};

/**
 * 여러 미디어 URL의 접근성 확인
 * @param {Array<Object>} uploadedMedia - 업로드된 미디어 정보 배열
 * @returns {Promise<void>}
 */
export const verifyMediaReadiness = async (uploadedMedia) => {
  console.log(`🔍 Verifying ${uploadedMedia.length} media URLs...`);
  
  for (const media of uploadedMedia) {
    console.log(`Checking media: ${media.fileName || media.path}`);
    
    // Firebase Storage URL인지 확인
    const isFirebaseUrl = media.url.includes('firebasestorage.googleapis.com');
    
    if (isFirebaseUrl) {
      // Firebase Storage URL 준비 상태 확인
      await waitForFirebaseUrlReady(media.url);
    } else {
      // 외부 URL의 경우 단순 접근성 확인
      const accessible = await isUrlAccessible(media.url);
      if (!accessible) {
        throw new Error(`Media URL not accessible: ${media.url}`);
      }
    }
  }
  
  console.log(`✅ All ${uploadedMedia.length} media URLs are ready`);
};

/**
 * 이미지 리사이징 (브라우저에서 처리)
 * @param {File} file - 리사이징할 이미지 파일
 * @param {number} maxWidth - 최대 너비
 * @param {number} quality - 품질 (0-1)
 * @returns {Promise<File>} 리사이징된 파일
 */
export const resizeImage = async (file, maxWidth = 1920, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        let { width, height } = img;
        
        // 리사이징이 필요한지 확인
        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // 이미지 그리기
        ctx.drawImage(img, 0, 0, width, height);
        
        // Blob으로 변환
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Failed to resize image'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};